import React, { useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";

/**
 * Modal custom pour afficher les erreurs de validation CSV
 */
function ValidationErrorsModal({ errors, onClose }) {
  if (!errors || errors.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-red-600">⚠️ Erreurs de validation du CSV</h3>
          <p className="text-sm text-gray-600 mt-1">Aucune modification n'a été appliquée. Veuillez corriger les erreurs ci-dessous.</p>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1">
          <ul className="space-y-3">
            {errors.map((err, idx) => (
              <li key={idx} className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                <strong>{err.type || "Erreur"} :</strong> {err.message}
                {err.qrCode && <div className="mt-1 text-xs text-red-600">QR Code: {err.qrCode}</div>}
              </li>
            ))}
          </ul>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal de résolution des conflits (chambres absentes de la DB)
 */
function ConflictResolutionModal({ conflicts, onResolve, onCancel }) {
  const [resolutions, setResolutions] = useState({});

  const allResolved = conflicts.every((c) => resolutions[c.csvRoomNumber]);
  const hasAnyWithExistingRoom = conflicts.some((c) => c.existingRoom);

  const setAction = (csvRoomNumber, action) => {
    setResolutions((prev) => ({ ...prev, [csvRoomNumber]: action }));
  };

  const applyToAll = (action) => {
    const next = {};
    conflicts.forEach((c) => {
      // "rename" n'est disponible que si le conflit a une existingRoom
      if (action === "rename" && !c.existingRoom) {
        next[c.csvRoomNumber] = "create";
      } else {
        next[c.csvRoomNumber] = action;
      }
    });
    setResolutions(next);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-orange-600">Chambres introuvables en base de données</h3>
          <p className="text-sm text-gray-600 mt-1">
            {conflicts.length} chambre{conflicts.length > 1 ? "s" : ""} du CSV n'exist{conflicts.length > 1 ? "ent" : "e"} pas encore. Choisissez une action pour chacune.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="text-xs text-gray-500 font-medium">Appliquer à tout :</span>
            {hasAnyWithExistingRoom && (
              <button
                onClick={() => applyToAll("rename")}
                className="px-2.5 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50 transition-colors"
              >
                Tout renommer
              </button>
            )}
            <button
              onClick={() => applyToAll("create")}
              className="px-2.5 py-1 text-xs rounded border border-green-300 text-green-700 hover:bg-green-50 transition-colors"
            >
              Tout créer
            </button>
            <button
              onClick={() => applyToAll("skip")}
              className="px-2.5 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Tout ignorer
            </button>
          </div>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1 space-y-4">
          {conflicts.map((conflict) => {
            const selected = resolutions[conflict.csvRoomNumber];
            return (
              <div key={conflict.csvRoomNumber} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold text-gray-800">Chambre {conflict.csvRoomNumber}</span>
                  {conflict.moduleNumber && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Module #{conflict.moduleNumber}</span>
                  )}
                  {conflict.csvQrCode && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">QR: {conflict.csvQrCode}</span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">ligne {conflict.lineNumber}</span>
                </div>

                {conflict.existingRoom ? (
                  <p className="text-sm text-gray-600 mb-3">
                    Le module <strong>#{conflict.moduleNumber}</strong> est actuellement associé à la chambre <strong>{conflict.existingRoom.rRoomNumber}</strong>.
                  </p>
                ) : conflict.moduleNumber ? (
                  <p className="text-sm text-gray-600 mb-3">
                    Le module <strong>#{conflict.moduleNumber}</strong> n'est associé à aucune chambre.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 mb-3">Aucun module dans cette ligne (QR uniquement).</p>
                )}

                <div className="flex flex-wrap gap-2">
                  {conflict.existingRoom && (
                    <button
                      onClick={() => setAction(conflict.csvRoomNumber, "rename")}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                        selected === "rename" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      Renommer «{conflict.existingRoom.rRoomNumber}» → «{conflict.csvRoomNumber}»
                    </button>
                  )}
                  <button
                    onClick={() => setAction(conflict.csvRoomNumber, "create")}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                      selected === "create" ? "bg-green-600 text-white border-green-600" : "bg-white text-green-700 border-green-300 hover:bg-green-50"
                    }`}
                  >
                    {conflict.existingRoom ? `Créer «${conflict.csvRoomNumber}» et déplacer le module` : `Créer la chambre «${conflict.csvRoomNumber}»`}
                  </button>
                  <button
                    onClick={() => setAction(conflict.csvRoomNumber, "skip")}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                      selected === "skip" ? "bg-gray-500 text-white border-gray-500" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Ignorer cette ligne
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {Object.keys(resolutions).length} / {conflicts.length} résolu{Object.keys(resolutions).length > 1 ? "s" : ""}
          </span>
          <div className="flex gap-3">
            <button onClick={onCancel} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">
              Annuler
            </button>
            <button
              onClick={() => onResolve(resolutions)}
              disabled={!allResolved}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Valider et voir le résumé
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal de confirmation avant d'appliquer les changements
 */
function ConfirmApplyModal({ preview, onConfirm, onCancel, isApplying }) {
  const [selectedToDelete, setSelectedToDelete] = useState(new Set());
  const [showActiveRooms, setShowActiveRooms] = useState(false);

  if (!preview) return null;

  const allAbsent = preview.deletableRooms || [];
  const deletable = showActiveRooms ? allAbsent : allAbsent.filter((r) => !r.hasActivity);
  const activeCount = allAbsent.filter((r) => r.hasActivity).length;

  const toggleRoom = (rId) => {
    setSelectedToDelete((prev) => {
      const next = new Set(prev);
      next.has(rId) ? next.delete(rId) : next.add(rId);
      return next;
    });
  };

  const selectAll = () => setSelectedToDelete((prev) => {
    const next = new Set(prev);
    deletable.forEach((r) => next.add(r.rId));
    return next;
  });
  const deselectAll = () => setSelectedToDelete((prev) => {
    const next = new Set(prev);
    deletable.forEach((r) => next.delete(r.rId));
    return next;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-green-600">✓ Validation réussie</h3>
          <p className="text-sm text-gray-600 mt-1">Le CSV est valide. Voulez-vous appliquer les changements suivants ?</p>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1">
          <div className="space-y-3">
            {preview.isPartial && (
              <div className="p-3 bg-yellow-50 border border-yellow-400 rounded-md text-sm text-yellow-800">
                <strong>Attention :</strong> le fichier contient {preview.csvRooms} ligne{preview.csvRooms > 1 ? "s" : ""} traitée{preview.csvRooms > 1 ? "s" : ""} sur {preview.totalRooms} chambres. Les chambres absentes du fichier ne seront pas modifiées.
              </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-gray-700">
                <strong>Chambres dans le fichier :</strong> {preview.csvRooms} / {preview.totalRooms}
              </p>
              {preview.roomsToRename > 0 && (
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Chambres à renommer :</strong> {preview.roomsToRename}
                </p>
              )}
              <p className="text-sm text-gray-700 mt-1">
                <strong>QR codes à créer / mettre à jour :</strong> {preview.qrCodesToUpdate}
              </p>
              {preview.modulesToAssociate > 0 && (
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Modules à associer / réassocier :</strong> <span className="text-blue-600 font-medium">{preview.modulesToAssociate}</span>
                </p>
              )}
              {preview.roomTypesToUpdate > 0 && (
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Types de chambre à mettre à jour :</strong> <span className="text-purple-600 font-medium">{preview.roomTypesToUpdate}</span>
                </p>
              )}
              {preview.roomsWithNoChange > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  <strong>Aucun changement :</strong> {preview.roomsWithNoChange} chambre{preview.roomsWithNoChange > 1 ? "s" : ""} déjà à jour
                </p>
              )}
            </div>

            {preview.roomsToRename > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">Voir les renommages</summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                  {preview.mappedRooms
                    .filter((r) => r.oldRoomNumber !== r.newRoomNumber)
                    .map((r, idx) => (
                      <div key={idx} className="text-xs text-gray-600 pl-4">
                        "{r.oldRoomNumber}" → "{r.newRoomNumber}"
                      </div>
                    ))}
                </div>
              </details>
            )}

            {preview.modulesToAssociate > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">Voir les associations modules-chambres</summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                  {preview.mappedRooms
                    .filter((r) => r.moduleNumber)
                    .map((r, idx) => (
                      <div key={idx} className="text-xs text-gray-600 pl-4">
                        Chambre "{r.newRoomNumber}" ← Module #{r.moduleNumber}
                      </div>
                    ))}
                </div>
              </details>
            )}

            {allAbsent.length > 0 && (
              <div className="border border-red-200 rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-red-50 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-sm font-medium text-red-700">
                    Chambres absentes du CSV ({allAbsent.length})
                    {selectedToDelete.size > 0 && (
                      <span className="ml-1 text-red-600 font-normal">— {selectedToDelete.size} à supprimer</span>
                    )}
                  </span>
                  <div className="flex items-center gap-1 ml-auto">
                    <button
                      onClick={() => setShowActiveRooms(false)}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${!showActiveRooms ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                    >
                      Sans activité{activeCount > 0 ? ` (${allAbsent.length - activeCount})` : ""}
                    </button>
                    <button
                      onClick={() => setShowActiveRooms(true)}
                      className={`px-2 py-0.5 text-xs rounded border transition-colors ${showActiveRooms ? "bg-gray-700 text-white border-gray-700" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                    >
                      Toutes{activeCount > 0 ? ` (${allAbsent.length})` : ""}
                    </button>
                  </div>
                </div>
                {deletable.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500 text-center">Aucune chambre dans ce filtre</div>
                ) : (
                  <>
                    <div className="px-3 py-1.5 bg-white border-b border-gray-100 flex gap-2">
                      <button onClick={selectAll} className="text-xs text-red-600 hover:underline">
                        Tout sélectionner
                      </button>
                      {Array.from(selectedToDelete).some((id) => deletable.find((r) => r.rId === id)) && (
                        <button onClick={deselectAll} className="text-xs text-gray-500 hover:underline">
                          Tout désélectionner
                        </button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-auto divide-y divide-gray-100">
                      {deletable.map((room) => (
                        <label key={room.rId} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedToDelete.has(room.rId)}
                            onChange={() => toggleRoom(room.rId)}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-gray-700">Chambre {room.rRoomNumber}</span>
                          <div className="ml-auto text-right space-y-0.5">
                            {[
                              { label: "HB", value: room.lastHeartbeat },
                              { label: "Douche", value: room.lastShower },
                              { label: "GM", value: room.lastStay },
                            ].map(({ label, value }) => (
                              <div key={label} className="flex items-center gap-1 justify-end">
                                <span className="text-xs text-gray-400">{label}</span>
                                <span className={`text-xs px-1.5 py-0 rounded-full ${value ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-400"}`}>
                                  {value ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {selectedToDelete.size > 0 && (
                  <div className="px-3 py-2 bg-red-50 border-t border-red-200">
                    <p className="text-xs text-red-600">
                      Ces chambres seront supprimées définitivement (QR codes, associations modules inclus).
                      {Array.from(selectedToDelete).some((id) => allAbsent.find((r) => r.rId === id && r.hasActivity)) && (
                        <span className="font-medium"> Certaines ont de l'activité — confirmer la suppression.</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onCancel} disabled={isApplying} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50">
            Annuler
          </button>
          <button
            onClick={() => onConfirm(Array.from(selectedToDelete))}
            disabled={isApplying}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isApplying ? "Application en cours..." : "Confirmer et appliquer"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Composant d'import CSV pour renommer les chambres et associer les QR codes
 */
function RoomsCsvImport({ masterId, onSuccess }) {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  const [file, setFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [preview, setPreview] = useState(null);
  const [conflicts, setConflicts] = useState(null); // non-null = modal conflits ouvert
  const [conflictResolutions, setConflictResolutions] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
      setValidationErrors(null);
      setPreview(null);
      setConflicts(null);
      setConflictResolutions({});
      setSuccessMessage(null);

      // Lancer la validation automatiquement
      await validateFile(selectedFile);
    } else if (selectedFile) {
      alert("Veuillez sélectionner un fichier CSV valide.");
      e.target.value = "";
    }
  };

  const validateFile = async (fileToValidate) => {
    if (!fileToValidate || !masterId) return;

    setIsValidating(true);
    setValidationErrors(null);
    setPreview(null);
    setConflicts(null);
    setConflictResolutions({});
    setSuccessMessage(null);

    try {
      const csvContent = await fileToValidate.text();

      const res = await axios.post(
        `${API_URL}/hotels/${masterId}/import-rooms-csv?dryRun=true`,
        { csvContent },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success && res.data.valid) {
        const previewData = res.data.preview;
        if (previewData.conflicts && previewData.conflicts.length > 0) {
          // Des chambres n'existent pas : montrer le modal de résolution d'abord
          setConflicts(previewData.conflicts);
          setPreview(previewData); // gardé pour le ConfirmApplyModal après
        } else {
          setPreview(previewData);
        }
      } else if (!res.data.valid && res.data.errors) {
        setValidationErrors(res.data.errors);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setValidationErrors([{ type: "unknown", message: "Erreur inconnue lors de la validation." }]);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || "Erreur lors de la validation du CSV.";
      setValidationErrors([{ type: "error", message: errMsg }]);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsValidating(false);
    }
  };

  const handleConflictsResolved = (resolutions) => {
    setConflictResolutions(resolutions);
    setConflicts(null); // ferme le modal conflits, ouvre le ConfirmApplyModal
  };

  const handleApply = async (roomsToDelete = []) => {
    if (!file || !masterId) return;

    setIsApplying(true);
    setValidationErrors(null);
    setSuccessMessage(null);

    try {
      const csvContent = await file.text();

      const res = await axios.post(
        `${API_URL}/hotels/${masterId}/import-rooms-csv?dryRun=false`,
        { csvContent, conflictResolutions, roomsToDelete },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success && res.data.valid) {
        const summary = res.data.summary || {};
        const parts = [];
        if (summary.roomsRenamed > 0) parts.push(`${summary.roomsRenamed} chambre(s) renommée(s)`);
        if (summary.qrRoomsUpdated > 0) parts.push(`${summary.qrRoomsUpdated} QR code(s) mis à jour`);
        if (summary.modulesAssociated > 0) parts.push(`${summary.modulesAssociated} module(s) associé(s)`);
        if (summary.roomTypesUpdated > 0) parts.push(`${summary.roomTypesUpdated} type(s) de chambre mis à jour`);
        if (summary.conflictsResolved > 0) parts.push(`${summary.conflictsResolved} conflit(s) résolu(s)`);
        if (summary.roomsDeleted > 0) parts.push(`${summary.roomsDeleted} chambre(s) supprimée(s)`);
        if (summary.roomsWithNoChange > 0) parts.push(`${summary.roomsWithNoChange} chambre(s) sans changement`);
        const partialNote = summary.isPartial ? ` (import partiel : ${summary.csvRooms}/${summary.totalRooms} chambres)` : "";
        const msg = `Import réussi${parts.length > 0 ? ` : ${parts.join(", ")}` : ""}${partialNote}.`;
        setSuccessMessage(msg);
        setPreview(null);
        setConflicts(null);
        setConflictResolutions({});
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (onSuccess) onSuccess();
      } else if (!res.data.valid && res.data.errors) {
        setValidationErrors(res.data.errors);
        setPreview(null);
        setConflicts(null);
        setConflictResolutions({});
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setValidationErrors([{ type: "unknown", message: "Erreur inconnue lors de l'application des changements." }]);
        setPreview(null);
        setConflicts(null);
        setConflictResolutions({});
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || "Erreur lors de l'application des changements.";
      setValidationErrors([{ type: "error", message: errMsg }]);
      setPreview(null);
      setConflicts(null);
      setConflictResolutions({});
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="mb-6">
      <label className="relative">
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} disabled={isValidating || isApplying} className="hidden" />
        <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm font-medium">{isValidating ? "⏳ Validation en cours..." : isApplying ? "⏳ Application en cours..." : "📄 Importer un CSV"}</span>
      </label>

      {successMessage && <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-md text-sm text-green-800">{successMessage}</div>}

      {/* Modal d'erreurs */}
      {validationErrors && (
        <ValidationErrorsModal
          errors={validationErrors}
          onClose={() => {
            setValidationErrors(null);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
      )}

      {/* Modal de résolution des conflits */}
      {conflicts && (
        <ConflictResolutionModal
          conflicts={conflicts}
          onResolve={handleConflictsResolved}
          onCancel={() => {
            setConflicts(null);
            setPreview(null);
            setConflictResolutions({});
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
      )}

      {/* Modal de confirmation (s'ouvre après résolution des conflits ou si pas de conflits) */}
      {preview && !conflicts && (
        <ConfirmApplyModal
          preview={preview}
          onConfirm={handleApply}
          onCancel={() => {
            setPreview(null);
            setConflicts(null);
            setConflictResolutions({});
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
          isApplying={isApplying}
        />
      )}
    </div>
  );
}

export default RoomsCsvImport;
